import type { GeometricAnalysisSetup } from '@frontend/api';
import {
  type PreparedFeature,
  PreparedFeedbackItem,
  type RawFeatureOrFeedback,
  utils,
} from '@frontend/partviewer';

import { FEATURE_INDICATOR, FEEDBACK_INDICATOR } from 'constants/all';

import { FEATURE_LOADING_TYPES } from 'modules/common/viewer/cadViewer/constants';

import type { CachedFeatureData, PreparedSetup } from './types';

/*
This function is responsible for taking features from the API and mapping them into a feature format compatible with the Part Viewer.

This is not a best practice and was implemented to maintain parity with existing functionality.

Long term, the viewer in the frontend core application should only be responsbible for mapping feature options and should not need to prepare and features for the way @frontend/partviewer wishes to interact with them.
*/
export const processFeatures = ({
  features,
  flatFeatures = [],
  cachedFeatureData,
}: {
  features: RawFeatureOrFeedback[];
  flatFeatures?: RawFeatureOrFeedback[];
  cachedFeatureData?: CachedFeatureData;
}): PreparedFeature[] => {
  const rawFeatures = [...features, ...flatFeatures];

  const processedFeatures: PreparedFeature[] = [];
  // parse features and collect them into ideal format for part model
  const nonUrlFeatures = rawFeatures.filter(
    (feature) => !feature.authenticatedUrl,
  );
  const urlFeatures = rawFeatures.filter((feature) => feature.authenticatedUrl);

  for (const rawFeature of nonUrlFeatures) {
    processedFeatures.push(
      utils.prepareRawFeature(rawFeature, FEATURE_INDICATOR, false, {}),
    );
  }

  for (const rawFeature of urlFeatures) {
    const cachedData =
      cachedFeatureData?.[FEATURE_LOADING_TYPES.FEATURE][rawFeature.id];
    processedFeatures.push(
      utils.prepareRawFeature(
        rawFeature,
        FEATURE_INDICATOR,
        cachedData ? false : true,
        cachedData || {},
      ),
    );
  }

  const outputFeatures: PreparedFeature[] = [];
  features.forEach(({ id, flatFeatureId }) => {
    const feature = processedFeatures.find(
      (processedFeature) => processedFeature.id === id,
    );
    if (flatFeatureId && feature) {
      const flatFeature = processedFeatures.find(
        (processedFeature) => processedFeature.id === flatFeatureId,
      );
      if (flatFeature) {
        feature.flatFeature = flatFeature;
      }
    }
    if (feature) {
      outputFeatures.push(feature);
    }
  });

  return outputFeatures;
};

/*
This function is responsible for taking feedback items from the API and mapping them into a feedback item format compatible with the Part Viewer.

This is not a best practice and was implemented to maintain parity with existing functionality.

Long term, the viewer in the frontend core application should only be responsbible for mapping feedback item options and should not need to prepare them for the way @frontend/partviewer wishes to interact with them.
*/
export const processFeedbackItems = ({
  feedbackItems,
  flatFeedbackItems = [],
  cachedFeatureData,
}: {
  feedbackItems: RawFeatureOrFeedback[];
  flatFeedbackItems?: RawFeatureOrFeedback[];
  cachedFeatureData?: CachedFeatureData;
}) => {
  const rawFeedbackItems = [...feedbackItems, ...flatFeedbackItems];
  const processedFeedbackItems: PreparedFeedbackItem[] = [];

  const nonUrlFeedback = rawFeedbackItems.filter(
    (item) => !item.authenticatedUrl,
  );
  const urlFeedback = rawFeedbackItems.filter((item) => item.authenticatedUrl);

  for (const rawFeedback of nonUrlFeedback) {
    processedFeedbackItems.push(
      utils.prepareRawFeedback(rawFeedback, FEEDBACK_INDICATOR, false, {}),
    );
  }

  for (const rawFeedback of urlFeedback) {
    const cachedData =
      cachedFeatureData?.[FEATURE_LOADING_TYPES.FEEDBACK][rawFeedback.id];
    processedFeedbackItems.push(
      utils.prepareRawFeedback(
        rawFeedback,
        FEEDBACK_INDICATOR,
        cachedData ? false : true,
        cachedData || {},
      ),
    );
  }

  const outputFeedbackItems: PreparedFeedbackItem[] = [];
  feedbackItems.forEach(({ id, flatFeedbackItemId }) => {
    const feedbackItem = processedFeedbackItems.find(
      (processedFeedbackItem) => processedFeedbackItem.id === id,
    );
    if (flatFeedbackItemId && feedbackItem) {
      const flatFeedbackItem = processedFeedbackItems.find(
        (processedFeedbackItem) =>
          processedFeedbackItem.id === flatFeedbackItemId,
      );
      if (flatFeedbackItem) {
        feedbackItem.flatFeature = flatFeedbackItem;
      }
    }
    if (feedbackItem) {
      outputFeedbackItems.push(feedbackItem);
    }
  });

  return outputFeedbackItems;
};

export const processSetups = (
  setups: GeometricAnalysisSetup[],
): PreparedSetup[] => {
  // transforms setups into form compatible with part viewer
  return setups.map((setup) => ({
    ...setup,
    featureKeys: setup.featureIds.map((id) =>
      utils.getFeatureKey(setup.referenceUuid, FEATURE_INDICATOR, id),
    ),
    feedbackKeys: setup.feedbackIds.map((id) =>
      utils.getFeatureKey(setup.referenceUuid, FEATURE_INDICATOR, id),
    ),
  }));
};
